import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestActivateCode } from '../billing/billing';
import { ActivationCodeRequest } from '../billing/billing.types';
import { libApiInstance } from '../axios';
import {
    requestOrderOfferByKey,
    requestOrderOffers,
    requestOrderStatus,
    requestRoloShareOrderOfferByKey,
} from '../offers/orders';

export function useGetOrderOffers() {
    return useQuery({
        queryKey: ['orderOffers'],
        queryFn: async ({ signal }) => (await requestOrderOffers(signal)).data,
    });
}

export function useGetOrderOfferByKey(offerKey: string | undefined) {
    return useQuery({
        queryKey: ['orderOffers', offerKey],
        enabled: !!offerKey,
        queryFn: async ({ signal }) => (await requestOrderOfferByKey(offerKey!, signal)).data,
    });
}

export function useGetOrderStatus(sessionId: string | undefined) {
    return useQuery({
        queryKey: ['stripeBilling', sessionId],
        queryFn: async ({ signal }) => (await requestOrderStatus(sessionId, signal)).data,
        enabled: !!sessionId,
        refetchInterval(query) {
            const data = query.state.data;
            if (data?.status === 'Success' || data?.status === 'PaymentFailed') {
                return false;
            }
            return 2000;
        },
    });
}

export function useSetFreePackage() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await libApiInstance.post('/roloshare/billing/setfreepackage');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['stripeBilling'] });
            queryClient.invalidateQueries({ queryKey: ['orderOffers'] });
            queryClient.invalidateQueries({ queryKey: ['userConfig'] });
        },
    });
}

export function useGetRoloshareOrderOfferByKey(offerKey: string | undefined) {
    return useQuery({
        queryKey: ['orderOffers', offerKey],
        enabled: !!offerKey,
        queryFn: async ({ signal }) =>
            (await requestRoloShareOrderOfferByKey(offerKey!, signal)).data,
    });
}

export function useRoloshareBillingActivateCode() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ActivationCodeRequest) => {
            await requestActivateCode(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['stripeBilling'] });
            queryClient.invalidateQueries({ queryKey: ['orderOffers'] });
            queryClient.invalidateQueries({ queryKey: ['userConfig'] });
        },
    });
}
